import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  Image,
  ActivityIndicator,
  RefreshControl,
  FlatList,
  TextInput,
  ScrollView,
  Platform,
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { getTukarPoinCatalog, logTukarPoinMenuOpen } from '../services/rewardsApi';
import AppHeader from '../components/AppHeader';
import {
  TUKAR_POIN_COLORS,
  catalogTabs,
  buildCatalogChips,
  matchesCatalogChip,
  matchesCatalogTab,
  formatPoin,
  formatPoinNumber,
  formatDateRange,
  catalogPoin,
  catalogStrikePoin,
  canClaim,
  isPromoConfigured,
  isInPromo,
  categoryBadge,
  catalogTags,
  isInStock,
  stockLabel,
} from '../utils/tukarPoin';

const FALLBACK_IMAGE = require('../../assets/rewards/reward-image-1.png');

function PointsStar() {
  return (
    <View style={styles.pointsStarOuter}>
      <Image 
        source={require('../../assets/icons/z-point-solid.svg')} 
        style={{ 
          width: 34, 
          height: 34, 
          tintColor: "#FFFFFF"
        }}
        resizeMode="contain"
      />
    </View>
  );
}

function CatalogPointIcon({ size = 14, muted = false }) {
  const star = Math.max(5, Math.round(size * 0.75));
  const bg = muted ? '#CBD5E1' : '#FBBF24';
  return (
    <View
      style={[
        styles.pointIcon,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: bg,
        },
      ]}
    >
      <Image 
        source={require('../../assets/icons/z-point-solid.svg')} 
        style={{ 
          width: star, 
          height: star, 
          tintColor: '#FFFFFF',
        }}
        resizeMode="contain"
      />
    </View>
  );
}

function DateLine({ text, promo, strike }) {
  if (!text) return null;
  const iconSize = strike ? 10 : 13;
  const color = strike ? '#CBD5E1' : promo ? '#F43F5E' : '#94A3B8';
  return (
    <View style={[styles.dateRow, strike && styles.dateRowStrike]}>
      <View style={styles.dateIconWrap}>
        <Ionicons name="calendar-outline" size={iconSize} color={color} />
        {strike ? <View style={styles.dateIconStrike} /> : null}
      </View>
      <Text
        style={[
          styles.dateText,
          promo && styles.dateTextPromo,
          strike && styles.dateTextStrike,
        ]}
      >
        {text}
      </Text>
    </View>
  );
}

function ListViewIcon({ active }) {
  const color = active ? '#FFFFFF' : '#94A3B8';
  return (
    <View style={styles.glyphCol}>
      <View style={[styles.listBar, { backgroundColor: color }]} />
      <View style={[styles.listBar, { backgroundColor: color, width: 8 }]} />
    </View>
  );
}

function CatalogTabs({ activeTab, onTabPress }) {
  return (
    <View style={styles.tabRow}>
      {catalogTabs.map((tab) => {
        const active = activeTab === tab.key;
        return (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tabBtn,
              active && styles.tabBtnActive,
              active && tab.key === 'promo' && styles.tabBtnHotDeals,
            ]}
            onPress={() => onTabPress(tab.key)}
          >
            <View style={styles.tabLabelRow}>
              {tab.icon === 'chili' ? (
                <Text style={[styles.tabChili, active ? styles.tabLabelHotDeals : styles.tabLabelInactive]}>
                  🌶️🌶️🌶️🌶️🌶️
                </Text>
              ) : null}
              <Text
                style={[
                  styles.tabLabel,
                  tab.icon === 'chili' && styles.tabLabelWithIcon,
                  active
                    ? (tab.key === 'promo' ? styles.tabLabelHotDeals : styles.tabLabelActive)
                    : styles.tabLabelInactive,
                ]}
              >
                {tab.label}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

export default function TukarPoinCatalogScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const [items, setItems] = useState([]);
  const [saldoPoin, setSaldoPoin] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('promo');
  const [chip, setChip] = useState('Semua');
  const [viewMode, setViewMode] = useState('grid');
  const searchRef = useRef(searchQuery);
  const itemsRef = useRef(items);
  searchRef.current = searchQuery;
  itemsRef.current = items;

  const loadCatalog = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await getTukarPoinCatalog({
        page: 1,
        limit: 200,
        search: opts.search ?? searchRef.current,
      });
      const data = response?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setSaldoPoin(Number(data.saldo_poin || 0));
    } catch (e) {
      setError(e.message || 'Gagal memuat katalog');
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (route.params?.tab === 'bisa_klaim' || route.params?.tab === 'promo') {
        setActiveTab(route.params.tab);
        setChip('Semua');
        navigation.setParams({ tab: undefined });
      }
      (async () => {
        await loadCatalog({ silent: itemsRef.current.length > 0 });
        logTukarPoinMenuOpen('club');
      })();
    }, [loadCatalog, navigation, route.params?.tab]),
  );

  const tabItems = useMemo(
    () => items.filter((item) => matchesCatalogTab(item, activeTab, saldoPoin)),
    [items, activeTab, saldoPoin],
  );
  const chips = useMemo(() => buildCatalogChips(tabItems), [tabItems]);

  useEffect(() => {
    if (chip !== 'Semua' && !chips.includes(chip)) {
      setChip('Semua');
    }
  }, [chips, chip]);

  const visibleItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tabItems.filter((item) => {
      if (q && !String(item.nama_reward || '').toLowerCase().includes(q)) return false;
      return matchesCatalogChip(item, chip);
    });
  }, [tabItems, chip, searchQuery]);

  const gridColumns = useMemo(() => {
    const left = [];
    const right = [];
    visibleItems.forEach((item, index) => {
      if (index % 2 === 0) left.push(item);
      else right.push(item);
    });
    return { left, right };
  }, [visibleItems]);

  const optimumDisplayLine = useMemo(() => {
    const optimumLength = 25;
    const lengths = visibleItems.map((item) => (item.nama_reward || '').length);
    const longest = Math.max(...lengths, 1);
    const optimumLine = Math.ceil(longest / optimumLength);
    return Math.max(1, optimumLine);
  }, [visibleItems]);

  const refreshControl = (
    <RefreshControl
      refreshing={refreshing}
      onRefresh={() => {
        setRefreshing(true);
        loadCatalog({ silent: true });
      }}
      tintColor={TUKAR_POIN_COLORS.goldDark}
    />
  );

  const emptyList = (
    <View style={styles.center}>
      <Text style={styles.emptyTitle}>Belum ada reward</Text>
      <Text style={styles.emptySub}>Coba tab atau filter lain.</Text>
    </View>
  );

  const openDetail = (item) => {
    navigation.navigate('TukarPoinDetail', { rewardId: item.id, preview: item, saldoPoin });
  };

  const renderCard = ({ item }) => {
    const badge = categoryBadge(item);
    const tags = catalogTags(item);
    const enough = canClaim(item, saldoPoin);
    const promoConfigured = isInPromo(item);
    const strikePoin = catalogStrikePoin(item);
    const inStock = isInStock(item);
    const stockText = stockLabel(item);
    const imageSource = item.gambar_url ? { uri: item.gambar_url } : FALLBACK_IMAGE;
    const isGrid = viewMode === 'grid';
    const period = formatDateRange(item.tanggal_mulai, item.tanggal_berakhir);
    const promoPeriod = promoConfigured
      ? formatDateRange(item.promo_tanggal_mulai, item.promo_tanggal_berakhir)
      : '';

    return (
      <TouchableOpacity
        style={[styles.card, isGrid && styles.gridCard]}
        activeOpacity={0.92}
        onPress={() => openDetail(item)}
      >
        <View style={[styles.imageWrap, (viewMode === 'list' && {height: 160})]}>
          <Image source={imageSource} style={styles.image} resizeMode="contain" />
          {promoConfigured ? (
            <LinearGradient
              colors={['#FFE4E6', '#F43F5E']}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
              style={styles.promoRibbon}
            >
              <Text style={styles.promoRibbonText}>Hot Deals</Text>
            </LinearGradient>
          ) : null}
        </View>
        <View style={[styles.cardBody, isGrid && styles.gridBody]}>
          {badge.label ? (
            <View style={styles.badgeRow}>
              <View style={[styles.catBadge, { backgroundColor: badge.bg }]}>
                <Text style={[styles.catBadgeText, { color: badge.color }]}>{badge.label}</Text>
              </View>
            </View>
          ) : null}
          <Text 
            style={[styles.cardTitle, {height: `${Math.min(item.display_line, optimumDisplayLine)}lh`}]} 
            numberOfLines={Math.min(item.display_line, optimumDisplayLine)} 
            minLines={Math.min(item.display_line, optimumDisplayLine)}
          >
            {item.nama_reward}
          </Text>
          <View style={styles.priceRowWarp}>
            <View style={styles.priceRow}>
              <CatalogPointIcon />
              <Text style={styles.price}>{formatPoin(catalogPoin(item))}</Text>
            </View>
            {strikePoin > 0 ? (
              <View style={styles.priceStrikeRow}>
                <CatalogPointIcon />
                <Text style={styles.priceStrike}>{formatPoin(strikePoin)}</Text>
                <View style={styles.priceStrikeLine} pointerEvents="none" />
              </View>
            ) : null}
          </View>
          {stockText ? <Text style={styles.stockText}>{stockText}</Text> : null}
          <View style={{ display: 'none' }}>
            <View style={styles.cardDivider} />
            <View>
              {promoPeriod ? <DateLine text={promoPeriod} promo /> : null}
              {period && promoConfigured ? (
                <DateLine text={period} strike />
              ) : period && !promoConfigured ? (
                <DateLine text={period} />
              ) : null}
            </View>
          </View>
          <View style={[styles.claimBtn, !(enough && inStock) && styles.claimBtnDisabled]}>
            <Text style={styles.claimBtnText}>{!inStock ? 'Stok habis' : 'Klaim'}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.safe}>
      <AppHeader title="Tukar Poin" showBack={false} />

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >

        <View style={styles.saldoSection}>
          <View style={styles.saldoCard}>
            <View style={styles.saldoEllipse} />
            <View style={styles.saldoContent}>
              <View style={styles.saldoMainRow}>
                <Text style={styles.saldoLabel}>Point Saya</Text>
                <View style={styles.saldoValueRow}>
                  <PointsStar />
                  <Text style={styles.saldoValue}>{formatPoinNumber(saldoPoin)}</Text>
                </View>
              </View>
              {/* Sementara disembunyikan
              <Text style={styles.saldoExpiry}>*Berlaku hingga 31 Desember 2026</Text>
              */}
            </View>
          </View>
          <LinearGradient
            colors={['rgba(15, 23, 42, 0.1)', 'rgba(15, 23, 42, 0.05)', 'rgba(15, 23, 42, 0.02)', 'rgba(15, 23, 42, 0)']}
            pointerEvents="none"
            style={styles.saldoShadowFade}
          />
        </View>

        <CatalogTabs
          activeTab={activeTab}
          onTabPress={(tab) => {
            setActiveTab(tab);
            setChip('Semua');
          }}
        />

        <View style={styles.searchRow}>
          <View style={styles.searchBox}>
            <Ionicons name="search" size={18} color="#94A3B8" />
            <TextInput
              style={styles.searchInput}
              placeholder="Cari reward disini"
              placeholderTextColor="#94A3B8"
              value={searchQuery}
              onChangeText={setSearchQuery}
              onSubmitEditing={() => loadCatalog({ search: searchQuery })}
              returnKeyType="search"
            />
          </View>
          <View style={styles.viewToggle}>
            <TouchableOpacity
              style={[styles.viewBtn, viewMode === 'list' && styles.viewBtnActive]}
              onPress={() => setViewMode('list')}
            >
              <ListViewIcon active={viewMode === 'list'} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.viewBtn, viewMode === 'grid' && styles.viewBtnActive]}
              onPress={() => setViewMode('grid')}
            >
              <Ionicons
                name="grid-outline"
                size={14}
                color={viewMode === 'grid' ? '#FFFFFF' : '#94A3B8'}
              />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.chipRowWrap}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipList}
            style={styles.chipRow}
          >
            {chips.map((item) => (
              <TouchableOpacity
                key={item}
                style={[styles.chip, chip === item && styles.chipActive]}
                onPress={() => setChip(item)}
              >
                <Text style={[styles.chipText, chip === item && styles.chipTextActive]}>{item}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {loading && items.length === 0 ? (
          <View style={styles.center}>
            <ActivityIndicator color={TUKAR_POIN_COLORS.goldDark} />
          </View>
        ) : error ? (
          <View style={styles.center}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryBtn} onPress={() => loadCatalog()}>
              <Text style={styles.retryText}>Coba lagi</Text>
            </TouchableOpacity>
          </View>
        ) : viewMode === 'grid' ? (
          <View style={[styles.list, styles.listContent]}>
            {visibleItems.length === 0 ? (
              emptyList
            ) : (
              <View style={styles.masonry}>
                {visibleItems.map((item) => (
                  <View key={String(item.id)}>{renderCard({ item })}</View>
                ))}
              </View>
            )}
          </View>
        ) : (
          <FlatList
            data={visibleItems}
            keyExtractor={(item) => String(item.id)}
            contentContainerStyle={styles.listContent}
            style={styles.list}
            renderItem={renderCard}
            showsVerticalScrollIndicator={false}
            refreshControl={refreshControl}
            ListEmptyComponent={emptyList}
          />
        )}

      </ScrollView>

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#FFFFFF' },
  scrollView: { flex: 1 },
  saldoSection: {
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 16,
    overflow: 'visible',
    zIndex: 2,
  },
  saldoShadowFade: {
    position: 'absolute',
    left: 8,
    right: 8,
    top: 90,
    height: 16,
    zIndex: 0,
  },
  saldoCard: {
    height: 90,
    borderRadius: 12,
    backgroundColor: '#F59E0B',
    paddingHorizontal: 16,
    justifyContent: 'center',
    overflow: 'hidden',
    zIndex: 1,
  },
  saldoEllipse: {
    position: 'absolute',
    width: 213,
    height: 213,
    borderRadius: 106.5,
    borderWidth: 24,
    borderColor: 'rgba(255, 255, 255, 0.24)',
    top: -43,
    left: -51,
    mixBlendMode: 'soft-light',
  },
  saldoContent: {
    zIndex: 1,
  },
  saldoMainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  saldoLabel: {
    fontFamily: 'Poppins-Medium',
    fontSize: 18,
    fontWeight: '500',
    color: '#F8FAFC',
    includeFontPadding: false,
  },
  saldoValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  saldoValue: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 30,
    fontWeight: '600',
    color: '#F8FAFC',
    lineHeight: 36,
    includeFontPadding: false,
  },
  saldoExpiry: {
    alignSelf: 'flex-end',
    marginTop: 2,
    fontFamily: 'Poppins',
    fontSize: 10,
    fontWeight: '400',
    fontStyle: 'italic',
    color: '#E2E8F0',
    includeFontPadding: false,
  },
  pointsStarOuter: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pointsStarInner: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#FBBF24',
    backgroundColor: '#FEF3C7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabRow: {
    flexDirection: 'row',
    width: '100%',
    marginBottom: 12,
  },
  tabBtn: {
    flex: 1,
    paddingVertical: 7.25,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: '#F1F5F9',
  },
  tabBtnActive: {
    borderBottomColor: '#000000',
  },
  tabBtnHotDeals: {
    borderBottomColor: '#F43F5E',
  },
  tabLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabChili: {
    fontSize: 9,
    lineHeight: 14,
    letterSpacing: -1,
  },
  tabLabel: {
    fontFamily: 'Poppins-Medium',
    fontSize: 13,
    fontWeight: '500',
    color: '#64748B',
  },
  tabLabelWithIcon: {
    marginLeft: 2,
  },
  tabLabelInactive: {
    opacity: 0.5,
  },
  tabLabelActive: {
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    color: '#1E293B',
    opacity: 1,
  },
  tabLabelHotDeals: {
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    color: '#F43F5E',
    opacity: 1,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    marginBottom: 8,
  },
  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 10.5 : 6.5,
    backgroundColor: '#FFFFFF',
  },
  searchInput: {
    flex: 1,
    fontFamily: 'Poppins',
    fontSize: 13,
    color: TUKAR_POIN_COLORS.navy,
  },
  viewToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 4,
    backgroundColor: '#FFFFFF',
    gap: 4,
  },
  viewBtn: {
    width: 26,
    height: 26,
    borderRadius: 4,
    padding: 4,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewBtnActive: {
    backgroundColor: '#334155',
  },
  glyphCol: { gap: 3, alignItems: 'flex-start' },
  listBar: { width: 12, height: 1.5, borderRadius: 1 },
  chipRowWrap: {
    flexGrow: 0,
    flexShrink: 0,
    marginBottom: 8,
  },
  chipRow: {
    flexGrow: 0,
    flexShrink: 0,
  },
  chipList: {
    paddingHorizontal: 20,
    alignItems: 'center',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 14,
    height: 28,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  chipActive: { backgroundColor: '#64748B' },
  chipText: {
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    lineHeight: 16,
    color: '#94A3B8',
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  chipTextActive: { color: '#FFFFFF' },
  list: { flex: 1 },
  listContent: {
    paddingHorizontal: 20,
    paddingBottom: Platform.OS === 'ios' ? 140 : 110,
  },
  masonry: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 8,
  },
  masonryCol: {
    flex: 1,
    gap: 8,
  },
  card: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  gridCard: {
    width: '100%',
    marginBottom: 0,
  },
  imageWrap: {
    width: '100%',
    height: 140,
    backgroundColor: '#FFFFFF',
    padding: 1,
  },
  image: { width: '100%', height: '100%' },
  promoRibbon: {
    position: 'absolute',
    top: 0,
    left: 0,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderBottomRightRadius: 6,
  },
  promoRibbonText: {
    color: '#FFFFFF',
    fontFamily: 'Poppins-Medium',
    fontSize: 10,
    fontWeight: '500',
    includeFontPadding: false,
  },
  cardBody: {
    flex: 1,
    paddingHorizontal: 10,
    paddingTop: 10,
    paddingBottom: 10,
    gap: 8,
  },
  gridBody: {
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 8,
    gap: 8,
  },
  badgeRow: { flexDirection: 'row' },
  catBadge: {
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  catBadgeText: {
    fontFamily: 'Poppins-Medium',
    fontSize: 10,
    fontWeight: '500',
    includeFontPadding: false,
  },
  cardTitle: {
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    fontWeight: '500',
    color: '#000000',
    lineHeight: 16,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  metaTag: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
    backgroundColor: '#F8FAFC',
  },
  metaTagText: {
    fontFamily: 'Poppins',
    fontSize: 10,
    color: '#64748B',
    includeFontPadding: false,
  },
  pointIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  priceRowWarp: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 8,
    rowGap: 4,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  price: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 13,
    fontWeight: '600',
    color: '#F59E0B',
    includeFontPadding: false,
  },
  priceStrikeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    position: 'relative',
  },
  priceStrike: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 13,
    fontWeight: '600',
    color: '#F59E0B',
    includeFontPadding: false,
  },
  priceStrikeLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    marginTop: -0.75,
    height: 1.5,
    backgroundColor: '#F43F5E',
  },
  stockText: {
    fontFamily: 'Poppins-Medium',
    fontSize: 10,
    fontWeight: '500',
    color: '#64748B',
    marginTop: 2,
    includeFontPadding: false,
  },
  cardDivider: {
    height: 1,
    backgroundColor: '#F1F5F9',
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  dateRowStrike: {
    marginTop: 2,
  },
  dateIconWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateIconStrike: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: '#CBD5E1',
  },
  dateText: {
    fontFamily: 'Poppins-Medium',
    fontSize: 10,
    fontWeight: '500',
    color: '#94A3B8',
    includeFontPadding: false,
  },
  dateTextPromo: {
    color: '#F43F5E',
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
  },
  dateTextStrike: {
    fontSize: 8,
    fontWeight: '500',
    color: '#CBD5E1',
    textDecorationLine: 'line-through',
  },
  claimBtn: {
    marginTop: 'auto',
    backgroundColor: '#1E293B',
    borderRadius: 6,
    paddingVertical: 6,
    alignItems: 'center',
  },
  claimBtnDisabled: {
    opacity: 0.5,
  },
  claimBtnText: {
    color: '#FFFFFF',
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    fontWeight: '500',
  },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  errorText: { fontFamily: 'Poppins', color: TUKAR_POIN_COLORS.error, textAlign: 'center' },
  retryBtn: {
    marginTop: 12,
    backgroundColor: TUKAR_POIN_COLORS.navy,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  retryText: { color: '#fff', fontFamily: 'Poppins-SemiBold' },
  emptyTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 16, color: TUKAR_POIN_COLORS.navy },
  emptySub: { fontFamily: 'Poppins', fontSize: 13, color: TUKAR_POIN_COLORS.slate, marginTop: 4 },
});
